import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { FleetPage } from './pages/Fleet/FleetPage';
import { ShipsPage } from './pages/Ships/ShipsPage';
import { StatsPage } from './pages/Stats/StatsPage';
import { ShipGarage } from './pages/ShipGarage/ShipGarage';
import { ToastContainer } from './components/common/ToastContainer';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { TutorialOverlay } from './components/tutorial/TutorialOverlay';
import { useTutorial } from './hooks/useTutorial';
import { authService } from './services/authService';

// Componente para manejar el retorno desde starmap
const AuthReturnHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthReturn = async () => {
      // Primero, intentar procesar token de la URL si existe
      const tokenProcessed = await authService.processTokenFromUrl();

      // Si hay un token (ya sea de sessionStorage o recién procesado de la URL)
      const authenticated = await authService.isAuthenticated();
      
      if (authenticated) {
        // Validar el token contra la API
        const token = await authService.getToken();
        if (token) {
          const validation = await authService.validateToken(token);
          if (!validation.valid) {
            console.error('❌ Token inválido según la API:', validation.error);
            // Limpiar token inválido y redirigir
            sessionStorage.removeItem('corvus_token');
            sessionStorage.removeItem('user_data');
            authService.redirectToStarmap();
            return;
          }
        }

        const returnUrl = sessionStorage.getItem('return_url');
        if (returnUrl) {
          try {
            const url = new URL(returnUrl);
            // Solo redirigir si es la misma aplicación
            if (url.origin === window.location.origin) {
              sessionStorage.removeItem('return_url');
              navigate(url.pathname + url.search, { replace: true });
              return;
            }
          } catch (error) {
            // Si hay error parseando la URL, simplemente limpiar
            sessionStorage.removeItem('return_url');
          }
        } else if (tokenProcessed) {
          // Si se procesó un token de la URL pero no hay return_url, quedarse en la página actual
          console.log('✅ Token procesado desde URL y validado, usuario autenticado');
        }
      }
    };

    handleAuthReturn();
  }, [navigate]);

  return null;
};

function AppContent() {
  // Tutorial global
  const {
    isActive: isTutorialActive,
    currentStep,
    isCompleted: isTutorialCompleted,
    hasNeverCompletedTutorial,
    startTutorial,
    nextStep,
    skipTutorial,
    getCurrentStep,
    triggerStepAction
  } = useTutorial();

  return (
    <div className="App">
      <AuthReturnHandler />
      <Routes>
          <Route path="/" element={<Navigate to="/flota" replace />} />
          <Route 
            path="/flota" 
            element={
              <ProtectedRoute>
                <FleetPage 
                  tutorialProps={{
                    isActive: isTutorialActive,
                    isCompleted: isTutorialCompleted,
                    hasNeverCompletedTutorial,
                    startTutorial,
                    triggerStepAction
                  }}
                />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/garage" 
            element={
              <ProtectedRoute>
                <ShipGarage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/catalogo" 
            element={
              <ProtectedRoute>
                <ShipsPage 
                  tutorialProps={{
                    isActive: isTutorialActive,
                    hasNeverCompletedTutorial,
                    startTutorial,
                    triggerStepAction
                  }}
                />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/stats" 
            element={
              <ProtectedRoute>
                <StatsPage />
              </ProtectedRoute>
            } 
          />
        </Routes>
        <ToastContainer />
        
        {/* Tutorial global */}
        <TutorialOverlay
          isActive={isTutorialActive}
          step={currentStep}
          targetElement={getCurrentStep()?.targetElement}
          message={getCurrentStep()?.message || ''}
          buttonText={getCurrentStep()?.buttonText}
          waitForAction={getCurrentStep()?.waitForAction}
          onNext={nextStep}
          onSkip={skipTutorial}
        />
      </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;